import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Download, RotateCcw, ChevronLeft } from 'lucide-react';
import { SkeletonDashboard } from '@/components/loading/Skeleton';
import { getCopilotHistoryInfoApi } from '@/api/copilot';
import { Badge } from '@/components/ui/badge';
import { EXTERNAL_USER_ID } from '@/config/env';
import dayjs from 'dayjs';
import useUserStore from '@/stores/userStore';
import { CopilotHistoryInfo, HistoryInsight } from '@/api/types';

interface HistoryInsightsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  followUserId: string;
  handleReapply: (insight: HistoryInsight) => void;
}

const HistoryInsightsDialog: React.FC<HistoryInsightsDialogProps> = ({
  open,
  onOpenChange,
  followUserId,
  handleReapply
}) => {
  const [insights, setInsights] = useState<CopilotHistoryInfo>([]);
  const { userInfo } = useUserStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(open);
      if (open) {
        try {
          const res = await getCopilotHistoryInfoApi({
            externalUserId: EXTERNAL_USER_ID,
            followUserId: followUserId
          });
          setInsights(res || []);
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 bottom-0  top-0 z-[111111] min-h-screen overflow-y-scroll bg-white pb-4">
      <div className="flex h-full flex-col bg-white px-3">
        <div className="relative my-6 w-full">
          <div
            className="absolute left-0 top-0 flex h-7 cursor-pointer items-center"
            onClick={() => onOpenChange(false)}
          >
            <ChevronLeft size={16} />
          </div>
          <div className="text-center text-xl font-semibold text-gray-900">历史洞察</div>
        </div>

        <div className="space-y-2">
          {loading ? (
            <div className="flex h-full w-full items-center justify-center">
              <SkeletonDashboard className="min-h-[80vh] w-full px-0" />
            </div>
          ) : insights.length === 0 ? (
            <div className="py-8 text-center text-gray-500">暂无历史洞察</div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* 头部操作栏 */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  {userInfo.customer_name}客户历史洞察记录
                </h2>
                <Button className="hidden items-center gap-2 bg-[#5959EB] text-white hover:bg-[#5959EB]/90">
                  <Download className="h-4 w-4" />
                  导出
                </Button>
              </div>

              {/* 历史洞察列表 */}
              <div className="min-w-full space-y-3 overflow-y-auto">
                {insights.map(insight => (
                  <div
                    key={JSON.stringify(insight)}
                    className="flex flex-col gap-2 rounded-lg border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-3"
                  >
                    {/* 日期时间和标签 */}
                    <div>
                      <div className="flex flex-wrap gap-2">
                        {insight.historyInfoList.map((tag, index) => (
                          <Badge
                            className="h-[30px] whitespace-nowrap rounded bg-[#5959EB] text-white"
                            key={index}
                          >
                            {tag.tabKey}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-left text-sm text-[#1F1F1F]">
                      {dayjs(insight.time).format('YYYY-MM-DD HH:mm:ss')}
                    </div>

                    {/* 触发对话 */}
                    <div>
                      <div className="flex items-start text-sm text-[#1F1F1F]">
                        <span className="text-left">触发对话:{insight.content}</span>
                      </div>
                    </div>

                    {/* 洞察内容 */}
                    <div className="flex items-start bg-[rgba(202,208,251,0.30)] px-2 py-1 text-sm text-[#1F1F1F]">
                      <span className="text-left">洞察内容:{insight.keyWord}</span>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex h-auto items-center gap-1 p-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          onClick={() => handleReapply(insight)}
                        >
                          <RotateCcw className="h-4 w-4" />
                          重新应用
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.querySelector('#root')
  );
};

export default HistoryInsightsDialog;
